import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationsContext';
import useBreakpoint from '../hooks/useBreakpoint';

const SIDEBAR_BG  = '#111';
const BLUE        = '#fff';
const FONT        = "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif";

/* ── Icon components ── */
const I = {
  dashboard: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  properties: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  tenants: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  payments: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  reminders: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
  profile: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  pricing: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  nebenkosten: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="9" y1="21" x2="9" y2="9"/>
    </svg>
  ),
  zaehler: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 8v4l2.5 2.5"/>
      <path d="M16.5 5.5l1-1"/>
      <path d="M7.5 5.5l-1-1"/>
    </svg>
  ),
  kontakte: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  finanzen: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
      <polyline points="16 7 22 7 22 13"/>
    </svg>
  ),
  nachrichten: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  dokumente: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  mietuebersicht: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  units: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="8" height="8" rx="1"/>
      <rect x="13" y="3" width="8" height="8" rx="1"/>
      <rect x="3" y="13" width="8" height="8" rx="1"/>
      <rect x="13" y="13" width="8" height="8" rx="1"/>
    </svg>
  ),
  vorgaenge: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  signout: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  hamburger: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  close: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  bell: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
};

const NAV_GROUPS = [
  {
    label: null,
    items: [
      { path: '/dashboard',  label: 'Dashboard',  icon: I.dashboard  },
      { path: '/properties', label: 'Objekte',    icon: I.properties },
      { path: '/units',      label: 'Einheiten',  icon: I.units      },
      { path: '/tenants',    label: 'Mieter',     icon: I.tenants    },
      { path: '/payments',   label: 'Zahlungen',  icon: I.payments   },
    ],
  },
  {
    label: null,
    items: [
      { path: '/vorgaenge',      label: 'Vorgänge',      icon: I.vorgaenge      },
      { path: '/zaehler',        label: 'Zähler',        icon: I.zaehler        },
      { path: '/nebenkosten',    label: 'Nebenkosten',   icon: I.nebenkosten    },
      { path: '/kontakte',       label: 'Kontakte',      icon: I.kontakte       },
      { path: '/mietuebersicht', label: 'Mietübersicht', icon: I.mietuebersicht },
    ],
  },
  {
    label: 'Finanzen',
    items: [
      { path: '/finanzen', label: 'Finanzen', icon: I.finanzen },
    ],
  },
  {
    label: 'Kommunikation',
    items: [
      { path: '/nachrichten', label: 'Nachrichten', icon: I.nachrichten },
      { path: '/documents',   label: 'Dokumente',   icon: I.dokumente   },
    ],
  },
];

const SETTINGS_ITEMS = [
  { path: '/profile', label: 'Profil',     icon: I.profile },
  { path: '/pricing', label: 'Abonnement', icon: I.pricing },
];

/* Mobile bottom nav */
const BOTTOM_NAV = [
  { path: '/dashboard',  label: 'Start',     icon: I.dashboard  },
  { path: '/properties', label: 'Objekte',   icon: I.properties },
  { path: '/tenants',    label: 'Mieter',    icon: I.tenants    },
  { path: '/payments',   label: 'Zahlungen', icon: I.payments   },
];

const PLAN_LABEL = { starter: 'Starter', professional: 'Pro', enterprise: 'Enterprise' };
const PLAN_COLORS = {
  starter:      { color: 'rgba(255,255,255,0.45)', bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.12)' },
  professional: { color: '#fff',                  bg: 'rgba(255,255,255,0.12)', border: 'rgba(255,255,255,0.2)' },
  enterprise:   { color: '#fbbf24',               bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.3)'  },
};

/* ── Notification Bell ── */
function NotificationBell() {
  const { notifications, unreadCount, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function fmt(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); if (!open && unreadCount > 0) markAllRead(); }}
        style={{
          background: open ? '#f5f5f5' : 'none',
          border: 'none', cursor: 'pointer',
          width: '36px', height: '36px', borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', color: '#888',
          transition: 'background 150ms ease, color 150ms ease',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.color = '#111'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#888'; }}
        aria-label="Benachrichtigungen"
      >
        {I.bell}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '7px', right: '7px',
            width: '7px', height: '7px', borderRadius: '50%',
            background: BLUE, border: '1.5px solid white',
          }} />
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '46px', right: 0, zIndex: 200,
          background: 'white', border: '1px solid #e5e5e5',
          borderRadius: '14px', width: '320px', maxHeight: '420px', overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          fontFamily: FONT,
        }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>
              Benachrichtigungen
            </span>
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: '13px', color: '#9ca3af' }}>
              Keine Benachrichtigungen
            </div>
          ) : notifications.map((n, i) => (
            <div key={n.id} style={{
              padding: '12px 18px',
              borderBottom: i < notifications.length - 1 ? '1px solid #f9fafb' : 'none',
              background: n.read_at ? 'white' : '#f8faff',
            }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                {!n.read_at && (
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: BLUE, flexShrink: 0, marginTop: '5px' }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: n.read_at ? 500 : 600, color: '#111', marginBottom: '2px' }}>{n.title}</div>
                  {n.body && <div style={{ fontSize: '12px', color: '#888', lineHeight: 1.5 }}>{n.body}</div>}
                  <div style={{ fontSize: '11px', color: '#d1d5db', marginTop: '6px' }}>{fmt(n.created_at)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Sidebar content ── */
function SidebarContent({ pathname, profile, subscription, onSignOut, onClose }) {
  const plan     = subscription?.plan || 'starter';
  const pc       = PLAN_COLORS[plan] || PLAN_COLORS.starter;
  const firstName  = profile?.first_name || profile?.full_name?.split(' ')[0] || '';
  const lastName   = profile?.last_name  || profile?.full_name?.split(' ').slice(1).join(' ') || '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ');
  const initials    = [firstName[0], lastName[0]].filter(Boolean).join('').toUpperCase() || '?';

  const settingsActive = SETTINGS_ITEMS.some(s => s.path === pathname);
  const [settingsOpen, setSettingsOpen] = useState(settingsActive);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', fontFamily: FONT }}>

      {/* Logo */}
      <div style={{
        padding: '20px 20px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '1.5px', color: 'white', lineHeight: 1 }}>
            IMMO<span style={{ color: BLUE }}>HQ</span>
          </div>
          {profile?.company_name && (
            <div style={{
              fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '5px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px',
            }}>
              {profile.company_name}
            </div>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(255,255,255,0.3)', padding: '4px', display: 'flex',
            borderRadius: '6px', transition: 'color 150ms ease',
          }}>
            {I.close}
          </button>
        )}
      </div>

      {/* Nav groups */}
      <nav style={{ flex: 1, padding: '8px 12px', overflowY: 'auto' }}>
        {NAV_GROUPS.map(({ label, items }, gi) => (
          <div key={label ?? gi} style={{ marginBottom: '2px' }}>
            {gi > 0 && (
              <div style={{
                borderTop: '1px solid rgba(255,255,255,0.07)',
                margin: '8px 4px',
              }} />
            )}
            {label && (
              <div style={{
                fontSize: '10px', fontWeight: 700, letterSpacing: '1px',
                textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)',
                padding: '0 10px', marginBottom: '4px', marginTop: '6px',
              }}>
                {label}
              </div>
            )}
            {items.map(({ path, label: l, icon }) => {
              const active = pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={onClose}
                  className={`nav-link${active ? ' active' : ''}`}
                >
                  <span className="nav-icon">{icon(active ? 'white' : 'rgba(255,255,255,0.5)')}</span>
                  {l}
                  {active && (
                    <span style={{
                      marginLeft: 'auto',
                      width: '5px', height: '5px', borderRadius: '50%',
                      background: BLUE, flexShrink: 0,
                    }} />
                  )}
                </Link>
              );
            })}
          </div>
        ))}

        {/* Einstellungen accordion */}
        <div style={{ marginBottom: '6px' }}>
          <div style={{
            fontSize: '10px', fontWeight: 700, letterSpacing: '1px',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)',
            padding: '0 10px', marginBottom: '4px', marginTop: '24px',
          }}>
            Einstellungen
          </div>
          <button
            onClick={() => setSettingsOpen(o => !o)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
              padding: '9px 10px', borderRadius: '8px', border: 'none',
              background: settingsOpen ? 'rgba(255,255,255,0.08)' : 'none',
              color: settingsOpen ? 'white' : 'rgba(255,255,255,0.55)',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              fontFamily: FONT, textAlign: 'left',
              transition: 'background 150ms ease, color 150ms ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.background = settingsOpen ? 'rgba(255,255,255,0.08)' : 'none'; e.currentTarget.style.color = settingsOpen ? 'white' : 'rgba(255,255,255,0.55)'; }}
          >
            <span style={{ flexShrink: 0 }}>
              {(c => (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              ))(settingsOpen ? 'white' : 'rgba(255,255,255,0.5)')}
            </span>
            <span style={{ flex: 1 }}>Einstellungen</span>
            {settingsActive && !settingsOpen && (
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: BLUE, flexShrink: 0 }} />
            )}
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round"
              style={{ flexShrink: 0, transition: 'transform 200ms ease', transform: settingsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {/* Sub-items */}
          {settingsOpen && (
            <div style={{ marginTop: '2px', paddingLeft: '12px' }}>
              {SETTINGS_ITEMS.map(({ path, label: l, icon }) => {
                const active = pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={onClose}
                    className={`nav-link${active ? ' active' : ''}`}
                    style={{ paddingLeft: '10px' }}
                  >
                    <span className="nav-icon">{icon(active ? 'white' : 'rgba(255,255,255,0.5)')}</span>
                    {l}
                    {active && (
                      <span style={{
                        marginLeft: 'auto',
                        width: '5px', height: '5px', borderRadius: '50%',
                        background: BLUE, flexShrink: 0,
                      }} />
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </nav>

      {/* User footer */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '14px 16px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: `linear-gradient(135deg, ${BLUE}80, ${BLUE})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: 700, color: 'white', flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ overflow: 'hidden', flex: 1 }}>
            {displayName && (
              <div style={{
                fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.85)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {displayName}
              </div>
            )}
            <Link to="/pricing" onClick={onClose} style={{
              fontSize: '10px', fontWeight: 600, letterSpacing: '0.3px',
              textDecoration: 'none', padding: '1px 7px', borderRadius: '100px',
              color: pc.color, background: pc.bg, border: `1px solid ${pc.border}`,
              display: 'inline-block', marginTop: '3px',
            }}>
              {PLAN_LABEL[plan]}
            </Link>
          </div>
          <button onClick={onSignOut} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
            color: 'rgba(255,255,255,0.25)', fontFamily: 'inherit',
            borderRadius: '6px', display: 'flex', alignItems: 'center',
            transition: 'color 150ms ease',
          }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
            title="Abmelden"
          >
            {I.signout('currentColor')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Layout ── */
export default function Layout({ children }) {
  const { profile, subscription, signOut } = useAuth();
  const { pathname } = useLocation();
  const navigate     = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isNarrow = isMobile || isTablet;

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const currentNavItem = NAV_GROUPS.flatMap(g => g.items).find(n => n.path === pathname);
  const currentLabel = currentNavItem?.label || 'ImmoHQ';

  return (
    <div style={{
      display: 'flex', height: '100vh',
      fontFamily: FONT,
      background: '#f7f7f7',
    }}>

      {/* Desktop sidebar */}
      {!isNarrow && (
        <aside style={{
          width: '256px', background: SIDEBAR_BG,
          display: 'flex', flexDirection: 'column', flexShrink: 0,
          borderRight: '1px solid rgba(255,255,255,0.04)',
        }}>
          <SidebarContent
            pathname={pathname} profile={profile}
            subscription={subscription} onSignOut={handleSignOut} onClose={null}
          />
        </aside>
      )}

      {/* Mobile drawer overlay */}
      {isNarrow && (
        <>
          <div
            className="immo-overlay"
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 40,
              background: 'rgba(15,23,42,0.6)',
              backdropFilter: 'blur(2px)',
              opacity: drawerOpen ? 1 : 0,
              pointerEvents: drawerOpen ? 'auto' : 'none',
            }}
          />
          <div
            className={`immo-drawer ${drawerOpen ? 'immo-drawer-open' : 'immo-drawer-closed'}`}
            style={{
              position: 'fixed', top: 0, left: 0, bottom: 0, width: '256px',
              background: SIDEBAR_BG, zIndex: 50,
              display: 'flex', flexDirection: 'column',
              borderRight: '1px solid rgba(255,255,255,0.04)',
            }}
          >
            <SidebarContent
              pathname={pathname} profile={profile}
              subscription={subscription} onSignOut={handleSignOut}
              onClose={() => setDrawerOpen(false)}
            />
          </div>
        </>
      )}

      {/* Main content area */}
      <main style={{
        flex: 1, overflow: 'auto', background: '#f7f7f7',
        display: 'flex', flexDirection: 'column',
        paddingBottom: isMobile ? '80px' : 0,
      }}>

        {/* Top bar */}
        <div style={{
          background: 'white',
          borderBottom: '1px solid #e5e5e5',
          padding: isMobile ? '0 16px' : '0 28px',
          height: '56px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isNarrow && (
              <button
                onClick={() => setDrawerOpen(true)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '8px', color: '#888', display: 'flex', alignItems: 'center',
                  marginLeft: '-8px', borderRadius: '8px',
                  transition: 'background 150ms ease, color 150ms ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.color = '#111'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#888'; }}
                aria-label="Menü öffnen"
              >
                {I.hamburger}
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {currentNavItem && (
                <span style={{ color: '#9ca3af' }}>
                  {currentNavItem.icon('#9ca3af')}
                </span>
              )}
              <span style={{
                fontSize: '14px', fontWeight: 600,
                color: '#111',
              }}>
                {currentLabel}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!isMobile && (
              <span style={{ fontSize: '12px', color: '#9ca3af', marginRight: '4px' }}>
                {new Date().toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}
              </span>
            )}
            <NotificationBell />
          </div>
        </div>

        {/* Trial banner */}
        {(() => {
          const isTrialing = subscription?.status === 'trialing';
          if (!isTrialing) return null;
          const daysLeft = subscription?.trial_ends_at
            ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
            : 14;
          return (
            <div style={{
              background: '#111',
              padding: '10px 28px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 12, flexShrink: 0,
            }}>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                Noch <b>{daysLeft} Tag{daysLeft !== 1 ? 'e' : ''}</b> kostenlos testen
              </span>
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
              <Link to="/pricing" style={{
                fontSize: 13, color: 'white', fontWeight: 700,
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                Jetzt Tarif upgraden →
              </Link>
            </div>
          );
        })()}

        {/* Page content */}
        <div
          className="page-enter"
          style={{
            padding: isMobile ? '20px 16px' : isTablet ? '28px 24px' : '36px 44px',
            flex: 1,
            maxWidth: '1280px',
            width: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      {isMobile && (
        <nav
          className="immo-bottom-nav"
          style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999,
            background: 'white', borderTop: '1px solid #e5e5e5',
            display: 'flex', height: '60px',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          {BOTTOM_NAV.map(({ path, label, icon }) => {
            const active = pathname === path;
            const clr = active ? BLUE : '#9ca3af';
            return (
              <Link
                key={path}
                to={path}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '3px',
                  textDecoration: 'none', color: clr,
                  transition: 'color 150ms ease',
                }}
              >
                {icon(clr)}
                <span style={{
                  fontSize: '9px', fontWeight: active ? 700 : 500,
                  letterSpacing: '0.3px', textTransform: 'uppercase',
                }}>
                  {label}
                </span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}
